import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "../assets/logo.webp";

const links = [
  { to: "/about", label: "About Us" },
  { to: "/what-we-do", label: "What We Do" },
  // { to: "/who-we-serve", label: "Who We Serve" },
  { to: "/who-we-empower", label: "Who We Empower" },
  { to: "/programs", label: "Programs", hasDropdown: true },
  { to: "/partnership", label: "Partnerships" },
];

const programSubLinks = [
  {
    to: "/programs/engineering-skill-development",
    title: "Engineering Skill Development",
    subtitle: "Enabling industry-ready engineers",
  },
  {
    to: "/programs/career-guidance-employability",
    title: "Career Guidance & Employability",
    subtitle: "Turning engineering potential into opportunity",
  },
  {
    to: "/programs/independent-engineer",
    title: "Independent Engineer Initiative",
    subtitle: "Consulting, freelancing & self-practice",
  },
  {
    to: "/programs/engineer-welfare-recognition",
    title: "Engineer Welfare & Recognition",
    subtitle: "Professional dignity, peer support & honors",
  },
  {
    to: "/programs/industry-academia-collaboration",
    title: "Industry–Academia Collaboration",
    subtitle: "Bridging institutions and industry",
  },
  {
    to: "/programs/technology-innovation-development",
    title: "Technology & Innovation Development",
    subtitle: "AI, automation & future engineering",
  },
  {
    to: "/programs/engineering-community-development",
    title: "Engineering Community Development",
    subtitle: "Local chapters, meetups & networks",
  },
  {
    to: "/programs/india-engineering-growth-mission",
    title: "India Engineering Growth Mission",
    subtitle: "National development & technical capacity",
  },
];

function desktopNavLinkClass({ isActive }) {
  return [
    "whitespace-nowrap font-medium transition text-[14px] xl:text-lg",
    isActive ? "text-black font-semibold" : "text-gray-600 hover:text-black",
  ].join(" ");
}

function mobileNavLinkClass({ isActive }) {
  return [
    "block rounded-md px-3 py-2 text-base transition",
    isActive
      ? "bg-slate-100 text-black font-semibold"
      : "text-gray-600 hover:bg-slate-100",
  ].join(" ");
}

function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);
  const timeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const isProgramsActive = location.pathname.startsWith("/program");

  // Close menus on route change
  useEffect(() => {
    setDesktopDropdownOpen(false);
    setMobileProgramsOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  // Close desktop dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDesktopDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setDesktopDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setDesktopDropdownOpen(false);
    }, 180);
  };

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white px-3 sm:px-5 lg:px-6 xl:px-10">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 py-2 sm:gap-3 lg:gap-4 lg:py-2.5 xl:gap-6 xl:py-3">
        {/* Hamburger — phone/tablet only */}
        <button
          type="button"
          className="order-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg hover:bg-slate-100 lg:hidden"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? (
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        {/* Logo */}
        <div className="order-2 flex min-w-0 flex-1 items-center justify-center lg:order-none lg:flex-none lg:justify-start">
          <Link to="/" onClick={() => setMenuOpen(false)} className="leading-none">
            <img
              src={logo}
              alt="A.S.S.E.T"
              width={317}
              height={83}
              className="h-14 w-auto cursor-pointer object-contain sm:h-16 lg:h-12 xl:h-14"
            />
          </Link>
        </div>

        {/* Desktop links — compact at lg, roomier at xl */}
        <ul className="order-3 hidden min-w-0 flex-1 items-center justify-center gap-4 lg:order-none lg:flex xl:gap-8">
          {links.map(({ to, label, hasDropdown }) => {
            if (hasDropdown) {
              return (
                <li
                  key={to}
                  className="relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  ref={dropdownRef}
                >
                  <NavLink
                    to={to}
                    className={() =>
                      [
                        "inline-flex items-center gap-1.5 whitespace-nowrap font-medium transition text-[14px] xl:text-lg",
                        isProgramsActive
                          ? "text-black font-semibold"
                          : "text-gray-600 hover:text-black",
                      ].join(" ")
                    }
                  >
                    <span>{label}</span>
                    <svg
                      className={`h-4 w-4 transition-transform duration-200 ${
                        desktopDropdownOpen ? "rotate-180 text-black" : "text-gray-400 group-hover:text-black"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </NavLink>

                  {/* Desktop Dropdown Panel */}
                  {desktopDropdownOpen && (
                    <div className="absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2">
                      <div className="w-[620px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-xl shadow-slate-200/60 ring-1 ring-black/5 animate-in fade-in zoom-in-95 duration-150">
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1 p-3">
                          {programSubLinks.map((sub) => {
                            const isSubActive = location.pathname === sub.to;
                            return (
                              <Link
                                key={sub.to}
                                to={sub.to}
                                onClick={() => setDesktopDropdownOpen(false)}
                                className={`group flex flex-col rounded-xl p-2.5 transition text-left ${
                                  isSubActive
                                    ? "bg-blue-50/80 text-[#1565D8]"
                                    : "hover:bg-slate-50"
                                }`}
                              >
                                <span
                                  className={`text-[13.5px] font-semibold leading-snug transition-colors ${
                                    isSubActive
                                      ? "text-[#1565D8]"
                                      : "text-slate-800 group-hover:text-[#1565D8]"
                                  }`}
                                >
                                  {sub.title}
                                </span>
                                <span className="mt-0.5 text-[11.5px] leading-tight text-slate-500 line-clamp-1">
                                  {sub.subtitle}
                                </span>
                              </Link>
                            );
                          })}
                        </div>

                        {/* Dropdown Footer */}
                        {/* <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/90 px-4 py-2.5">
                          <span className="text-xs font-medium text-slate-500">
                            8 Specialized Programs
                          </span>
                          <Link
                            to="/programs"
                            onClick={() => setDesktopDropdownOpen(false)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#1565D8] transition hover:text-[#1255b8]"
                          >
                            <span>Explore All Programs</span>
                            <svg
                              className="h-3.5 w-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </Link>
                        </div> */}
                      </div>
                    </div>
                  )}
                </li>
              );
            }

            return (
              <li key={to}>
                <NavLink to={to} className={desktopNavLinkClass}>
                  {label}
                </NavLink>
              </li>
            );
          })}
        </ul>

        <Link
          to="/contact-us"
          onClick={() => setMenuOpen(false)}
          className="order-3 shrink-0 rounded-lg bg-[#1565D8] px-2.5 py-1.5 text-[12px] font-medium whitespace-nowrap text-white shadow transition hover:bg-[#1255b8] sm:px-4 sm:py-2 sm:text-[15px] lg:order-none lg:px-3.5 lg:py-2 lg:text-[14px] xl:px-5 xl:py-2.5 xl:text-[16px]"
        >
          Contact Us
        </Link>
      </div>

      {/* Mobile Drawer */}
      {menuOpen && (
        <div className="max-h-[calc(100vh-80px)] overflow-y-auto border-t border-slate-200 bg-white lg:hidden">
          <ul className="flex flex-col gap-1 px-4 py-3">
            {links.map(({ to, label, hasDropdown }) => {
              if (hasDropdown) {
                return (
                  <li key={to} className="flex flex-col">
                    <div
                      className={`flex items-center justify-between rounded-md px-3 py-2 text-base transition ${
                        isProgramsActive ? "bg-slate-100 text-black font-semibold" : "text-gray-600 hover:bg-slate-100"
                      }`}
                    >
                      <NavLink
                        to={to}
                        onClick={() => setMenuOpen(false)}
                        className="flex-1 font-medium"
                      >
                        {label}
                      </NavLink>
                      <button
                        type="button"
                        aria-label="Toggle programs submenu"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMobileProgramsOpen((prev) => !prev);
                        }}
                        className="p-1 text-gray-500 hover:text-black"
                      >
                        <svg
                          className={`h-4 w-4 transition-transform duration-200 ${
                            mobileProgramsOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Mobile Accordion */}
                    {mobileProgramsOpen && (
                      <div className="my-1 ml-3 flex flex-col space-y-1 border-l-2 border-slate-200 pl-3">
                        <NavLink
                          to="/programs"
                          onClick={() => setMenuOpen(false)}
                          className={({ isActive }) =>
                            `py-1.5 text-[13.5px] font-semibold transition ${
                              isActive ? "text-[#1565D8]" : "text-slate-700 hover:text-black"
                            }`
                          }
                        >
                          Overview (All Programs)
                        </NavLink>
                        {programSubLinks.map((sub) => (
                          <NavLink
                            key={sub.to}
                            to={sub.to}
                            onClick={() => setMenuOpen(false)}
                            className={({ isActive }) =>
                              `py-1.5 text-[13.5px] transition ${
                                isActive ? "text-[#1565D8] font-semibold" : "text-slate-600 hover:text-black"
                              }`
                            }
                          >
                            {sub.title}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </li>
                );
              }

              return (
                <li key={to}>
                  <NavLink
                    to={to}
                    className={mobileNavLinkClass}
                    onClick={() => setMenuOpen(false)}
                  >
                    {label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </nav>
  );
}

export default Nav;
